import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getCreditPackages, type CreditPackage } from "@/lib/api-client";
import { useTranslation } from "react-i18next";
import { formatPrice, formatNumber } from "@/lib/formatters";

const PricingPage = () => {
  const { t } = useTranslation("marketing");
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCreditPackages()
      .then((data) => {
        if (!cancelled) setPackages(data);
      })
      .catch(() => {
        if (!cancelled) setPackages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedPackages = [...packages].sort(
    (a, b) => a.sort_order - b.sort_order,
  );
  const middleIndex = Math.floor((sortedPackages.length - 1) / 2);

  const actionCosts = [
    {
      name: t("pricing.action_standardSearch"),
      cost: t("pricing.cost_free"),
    },
    {
      name: t("pricing.action_extendedSearch"),
      cost: t("pricing.cost_5credits"),
    },
    { name: t("pricing.action_csvExport"), cost: t("pricing.cost_2credits") },
    {
      name: t("pricing.action_gewerbeSearch"),
      cost: t("pricing.cost_3credits"),
    },
    { name: t("pricing.action_enrichment"), cost: t("pricing.cost_free") },
  ];

  return (
    <div>
      <section className="container py-16 md:py-24">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <h1 className="mb-4 text-4xl font-bold tracking-tight">
            {t("pricing.title")}
          </h1>
          <p className="text-lg text-muted-foreground">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Signup bonus CTA */}
        <div className="mx-auto mb-12 max-w-md rounded-xl border border-primary/20 bg-primary/5 p-6 text-center">
          <p className="mb-1 text-2xl font-bold">
            {t("pricing.freeBonusCredits")}
          </p>
          <p className="mb-4 text-sm text-muted-foreground">
            {t("pricing.freeBonusDescription")}
          </p>
          <Button asChild>
            <Link to="/signup">{t("pricing.freeBonusCta")}</Link>
          </Button>
        </div>

        {/* Credit packages */}
        {!loading && sortedPackages.length > 0 && (
          <div className="mx-auto mb-16 max-w-4xl">
            <h2 className="mb-8 text-center text-2xl font-semibold">
              {t("pricing.packagesTitle")}
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {sortedPackages.map((pkg, idx) => {
                const isHighlighted = idx === middleIndex;
                return (
                  <Card
                    key={pkg.id}
                    className={`relative border bg-card shadow-card transition-shadow hover:shadow-md ${
                      isHighlighted ? "ring-2 ring-primary" : ""
                    }`}
                  >
                    {isHighlighted && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                        {t("pricing.recommended")}
                      </Badge>
                    )}
                    <CardHeader className="pb-4">
                      <CardTitle className="text-xl">{pkg.name}</CardTitle>
                      <div className="mt-2">
                        <span className="text-3xl font-bold">
                          {formatNumber(pkg.credits)}
                        </span>
                        <span className="ml-1 text-muted-foreground">
                          {t("pricing.creditsUnit")}
                        </span>
                      </div>
                      <p className="mt-1 text-lg font-semibold text-primary">
                        {formatPrice(pkg.price_cents, pkg.currency)}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <Button
                        asChild
                        variant={isHighlighted ? "default" : "outline"}
                        className="w-full"
                      >
                        <a href="mailto:kontakt@firmenfinden.org">
                          {t("pricing.contactCta")}
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Action cost table */}
        <div className="mx-auto max-w-2xl">
          <h2 className="mb-8 text-center text-2xl font-semibold">
            {t("pricing.actionCostsTitle")}
          </h2>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    {t("pricing.tableAction")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    {t("pricing.tableCost")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {actionCosts.map((action) => (
                  <tr
                    key={action.name}
                    className="border-b border-border last:border-0"
                  >
                    <td className="flex items-center gap-2 px-4 py-3">
                      <Check className="h-4 w-4 shrink-0 text-primary" />
                      {action.name}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {action.cost}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA for logged-in users */}
        <div className="mx-auto mt-12 max-w-md text-center">
          <p className="mb-3 text-sm text-muted-foreground">
            {t("pricing.alreadySignedIn")}
          </p>
          <Button asChild variant="outline">
            <Link to="/credits">{t("pricing.buyCredits")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
